import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Box, Grid, MenuItem, Select, Stack } from "@mui/material";
import {
  FiActivity,
  FiAlertTriangle,
  FiBarChart2,
  FiClock,
  FiPieChart,
  FiTrendingUp,
  FiTruck,
  FiUsers,
} from "react-icons/fi";
import dayjs from "dayjs";

import useStyles from "./styles";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { paths } from "../../routes/paths";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomButtonTable } from "../../components/CustomButtonTable/CustomButtonTable";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import {
  ChartCard,
  StatCard,
  FleetStatusDonut,
  BrandPie,
  EnergyMixCard,
  ContractEndingsCard,
  ClaimsTrendChart,
  TrendingList,
  ThemeToggle,
} from "../../components/dashboard";
import { LanguageSwitcher } from "../../components/LanguageSwitcher/LanguageSwitcher";

import { useGetAllNotification } from "../../api/notification";
import { useGetAllSinisters } from "../../api/sinistre";
import { useGetDashboardAnalytics, StatusDistribution } from "../../api/dashboard";
import { useGetVehiclesByStatus } from "../../api/tco";
import { useGetAllVehiculesFleet } from "../../api/vehicule";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { getColumnsNotification } from "../Notification/ListNotification/notificationColumns";
import { getColumnsClaimsList } from "../Claims/ListClaims/columnsClaimsList";

const STATUS_LABEL_KEYS: Record<string, string> = {
  O: "dashboard.status.ordered",
  A: "dashboard.status.assigned",
  F: "dashboard.status.available",
  W: "dashboard.status.pending",
};

export const Dashboard: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fleetId = getCurrentFleetId();

  const { notifications } = useGetAllNotification(fleetId);
  const { sinistres } = useGetAllSinisters(fleetId);

  // Keyes sur `t` : les en-tetes de ces deux tableaux etaient des constantes
  // de module en francais en dur, d'ou "NOM DU COLLABORATEUR" et "STATUT" en
  // session anglaise.
  const notificationColumns = useMemo(() => getColumnsNotification(t), [t]);
  const claimsColumns = useMemo(() => getColumnsClaimsList(t), [t]);
  const { analytics, isPending } = useGetDashboardAnalytics(fleetId);

  const { vehiclesByStatus } = useGetVehiclesByStatus(fleetId);
  const { totalCount, vehicules } = useGetAllVehiculesFleet(fleetId, 1, 1000);

  const view = useMemo(() => {
    if (analytics) {
      return {
        kpis: analytics.kpis,
        statusDistribution: analytics.statusDistribution,
        brandDistribution: analytics.brandDistribution,
        energyDistribution: analytics.energyDistribution,
        sinisterTrend: analytics.sinisterTrend,
        sinisterDelta: analytics.sinisterDelta,
      };
    }

    const counts: Record<string, number> = { O: 0, A: 0, F: 0, W: 0 };
    (vehiclesByStatus ?? []).forEach((v: any) => {
      counts[v.vehicle_status] = Number(v.count) || 0;
    });
    const total = totalCount ?? Object.values(counts).reduce((a, b) => a + b, 0);
    const statusDistribution: StatusDistribution[] = Object.keys(counts).map((s) => ({
      status: s,
      label: s, // placeholder; localizedStatusDistribution translates by status code
      count: counts[s],
    }));

    return {
      kpis: {
        totalVehicles: total,
        assigned: counts.A,
        free: counts.F,
        ordered: counts.O,
        waiting: counts.W,
        collaborators: 0,
        activeCollaborators: 0,
        openSinisters: (sinistres ?? []).length,
        monthlyFleetCost: 0,
        utilizationRate: total > 0 ? Math.round((counts.A / total) * 1000) / 10 : 0,
      },
      statusDistribution,
      brandDistribution: (() => {
        const bc: Record<string, number> = {};
        (vehicules ?? []).forEach((v: any) => { const b = (v?.brand ?? "Unknown").toString(); bc[b] = (bc[b] ?? 0) + 1; });
        return Object.entries(bc).map(([brand, count]) => ({ brand, count })).sort((a, b) => b.count - a.count);
      })(),
      energyDistribution: (() => {
        const ec: Record<"thermal" | "hybrid" | "electric", number> = { thermal: 0, hybrid: 0, electric: 0 };
        (vehicules ?? []).forEach((v: any) => {
          const fuel = (v?.carburant ?? "").toString().toLowerCase();
          if (fuel.includes("electr")) ec.electric += 1;
          else if (fuel.includes("hybrid")) ec.hybrid += 1;
          else ec.thermal += 1;
        });
        const energyTotal = ec.thermal + ec.hybrid + ec.electric;
        return (["thermal", "hybrid", "electric"] as const).map((type) => ({
          type,
          count: ec[type],
          percentage: energyTotal > 0 ? Math.round((ec[type] / energyTotal) * 1000) / 10 : 0,
        }));
      })(),
      sinisterTrend: [] as { month: string; ym: string; count: number }[],
      sinisterDelta: 0,
    };
  }, [analytics, vehiclesByStatus, totalCount, sinistres, vehicules]);

  const { kpis } = view;

  // Re-localize status labels from the reliable status code (O/A/F/W).
  // The backend nalytics.statusDistribution[].label is English; the code is stable,
  // so we always translate on the client to respect the active language.
  const localizedStatusDistribution = useMemo(
    () =>
      view.statusDistribution.map((s) => ({
        ...s,
        label: t(STATUS_LABEL_KEYS[s.status] ?? s.status, s.label ?? s.status),
      })),
    [view.statusDistribution, t]
  );

  const [contractMonthsAhead, setContractMonthsAhead] = useState(6);

  const contractEndingRows = useMemo(() => {
    const horizon = dayjs().add(contractMonthsAhead, "month").endOf("day");
    const today = dayjs().startOf("day");
    return (vehicules ?? [])
      .filter((v: any) => v?.end_date)
      .map((v: any) => {
        const modelName =
          typeof v?.brandModel === "object"
            ? v?.brandModel?.name
            : (() => {
                try { return JSON.parse(v?.brandModel)?.name; } catch { return undefined; }
              })();
        return {
          id: v?.vehicleId ?? v?.id,
          title: [v?.brand, modelName].filter(Boolean).join(" ") || v?.immatriculation || t("dashboard.vehicleFallback", "Vehicle"),
          sublabel: v?.immatriculation ?? "",
          endDate: v.end_date,
        };
      })
      .filter((row) => {
        const end = dayjs(row.endDate);
        return end.isValid() && !end.isBefore(today) && !end.isAfter(horizon);
      })
      .sort((a, b) => dayjs(a.endDate).valueOf() - dayjs(b.endDate).valueOf());
  }, [vehicules, contractMonthsAhead]);

  const trendingRows = useMemo(
    () =>
      [...localizedStatusDistribution]
        .sort((a, b) => b.count - a.count)
        .map((s) => {
          const total = kpis.totalVehicles || 1;
          return {
            label: s.label,
            sublabel: `${s.count} ${t("dashboard.vehicle", { count: s.count })}`,
            delta: Math.round((s.count / total) * 100),
          };
        }),
    [localizedStatusDistribution, kpis.totalVehicles]
  );

  const moneyFmt = useMemo(
    () => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }),
    []
  );

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.vehiculeRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <CustomTypography variant="h2" text={t("dashboard.dashboard", "Dashboard")} isUppercase />
            <CustomTypography
              variant="default"
              text={t("dashboard.subtitle", "Fleet overview & operational intelligence")}
            />
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <LanguageSwitcher />
            <ThemeToggle />
            <CustomButtonTable
              variant="primary"
              textVariant="link"
              label={t("collaborator.addACollaborator", "add a collaborator")}
              textTransform="uppercase"
              width="fit-content"
              fontWeight="bold"
              textDecoration="none"
              iconName="plus"
              onClick={() => navigate(paths.collaborator.create)}
            />
            <CustomButtonTable
              variant="primary"
              textVariant="link"
              label={t("vehicule.addVehicule", "Add vehicule")}
              textTransform="uppercase"
              width="fit-content"
              fontWeight="bold"
              textDecoration="none"
              iconName="plus"
              onClick={() => navigate(paths.vehicle.create)}
            />
            <CustomButtonTable
              variant="primary"
              textVariant="link"
              label={t("panel.claims.toDeclareAsinister", "To declare a sinister")}
              textTransform="uppercase"
              width="fit-content"
              fontWeight="bold"
              textDecoration="none"
              iconName="plus"
              onClick={() => navigate(paths.claims.create)}
            />
          </Stack>
        </Stack>

        {/* KPI row */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={2.4}>
            <StatCard
              label={t("dashboard.totalVehicles", "Total vehicles")}
              value={kpis.totalVehicles}
              icon={<FiTruck size={18} />}
              color="primary"
              hint={t("dashboard.acrossFleet", "across the fleet")}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2.4}>
            <StatCard
              label={t("dashboard.utilization", "Utilization")}
              value={`${kpis.utilizationRate}%`}
              icon={<FiActivity size={18} />}
              color="success"
              hint={`${kpis.assigned} ${t("common.affected", "assigned")}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2.4}>
            <StatCard
              label={t("sidebar.colaborators", "Collaborators")}
              value={kpis.collaborators}
              icon={<FiUsers size={18} />}
              color="secondary"
              hint={`${kpis.activeCollaborators} ${t("common.active", "active")}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2.4}>
            <StatCard
              label={t("dashboard.openClaims", "Open claims")}
              value={kpis.openSinisters}
              icon={<FiAlertTriangle size={18} />}
              color="warning"
              delta={view.sinisterDelta}
              deltaSuffix={` ${t("dashboard.vsLastMonth", "vs last month")}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2.4}>
            <EnergyMixCard
              label={t("dashboard.energyMix", "Energy mix")}
              data={view.energyDistribution}
              labels={{
                thermal: t("dashboard.thermal", "Thermal"),
                hybrid: t("dashboard.hybrid", "Hybrid"),
                electric: t("dashboard.electric", "Electric"),
              }}
            />
          </Grid>
        </Grid>

        {/* Charts row 1 */}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <ChartCard
              eyebrow={t("dashboard.fleetComposition", "Fleet composition")}
              title={t("dashboard.vehiclesByBrand", "Vehicles by Brand")}
              subtitle={t("dashboard.vehiclesByBrandSub", "Where your fleet is concentrated")}
              icon={<FiBarChart2 size={18} />}
              minHeight={300}
            >
              <BrandPie data={view.brandDistribution} emptyLabel={t("dashboard.noData", "No data yet")} otherLabel={t("dashboard.others", "Others")} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} lg={5}>
            <ChartCard
              eyebrow={t("dashboard.operationalState", "Operational state")}
              title={t("dashboard.fleetByStatus", "Fleet by Status")}
              icon={<FiPieChart size={18} />}
              minHeight={300}
            >
              <FleetStatusDonut data={localizedStatusDistribution} centerLabel={t("sidebar.car", "vehicles")} />
            </ChartCard>
          </Grid>
        </Grid>

        {/* Charts row 2 */}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <ChartCard
              eyebrow={t("dashboard.momentum", "Momentum")}
              title={t("dashboard.claimsTrend", "Claims Trend")}
              subtitle={t("dashboard.claimsTrendSub", "Declared sinisters over the last 6 months")}
              icon={<FiTrendingUp size={18} />}
              actions={
                kpis.monthlyFleetCost > 0 ? (
                  <Box sx={{ textAlign: "right" }}>
                    <CustomTypography variant="default" text={t("notification.TCOPerMonth", "TCO / month")} />
                    <CustomTypography variant="h5" text={moneyFmt.format(kpis.monthlyFleetCost)} />
                  </Box>
                ) : undefined
              }
              minHeight={320}
            >
              <ClaimsTrendChart data={view.sinisterTrend} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} lg={5}>
            <ChartCard
              eyebrow={t("dashboard.breakdown", "Breakdown")}
              title={t("dashboard.statusShare", "Status Share")}
              subtitle={t("dashboard.statusShareSub", "Share of the fleet by status")}
              icon={<FiActivity size={18} />}
              minHeight={320}
            >
              <TrendingList rows={trendingRows} emptyLabel={isPending ? t("common.loading", "Loading...") : t("dashboard.noData", "No data yet")} />
            </ChartCard>
          </Grid>
        </Grid>

        {/* Charts row 3 — Contract endings */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ChartCard
              eyebrow={t("dashboard.renewals", "Renewals")}
              title={t("dashboard.contractEndings", "Contract endings")}
              subtitle={t("dashboard.contractEndingsSub", "Vehicle contracts ending within the selected window")}
              icon={<FiClock size={18} />}
              actions={
                <Select
                  size="small"
                  value={contractMonthsAhead}
                  onChange={(e) => setContractMonthsAhead(Number(e.target.value))}
                  sx={{ minWidth: 140, fontSize: 13 }}
                >
                  {[1, 3, 6, 9, 12].map((m) => (
                    <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>
                      {t("dashboard.nextNMonths", "Next {{count}} months", { count: m })}
                    </MenuItem>
                  ))}
                </Select>
              }
              minHeight={260}
            >
              <ContractEndingsCard
                rows={contractEndingRows}
                emptyLabel={t("dashboard.noContractEndings", "No contract ending in this window")}
                daysLeftLabel={t("dashboard.daysLeft", "days left")}
                onClickVehicle={(id) => navigate(paths.vehicle.update(id))}
              />
            </ChartCard>
          </Grid>
        </Grid>

        {/* Tables row */}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <CustomTable
              titleHeader={t("sidebar.notifications", "Notifications")}
              columns={notificationColumns}
              data={notifications}
              tableMaxHeight="234px"
              tableMinHeight="234px"
              labelButtonFooter={t("notification.seeAllNotifications", "See all notifications")}
              showPagination={false}
              onClickButtonFooter={() => navigate(paths.notifications.list)}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <CustomTable
              titleHeader={t("panel.claims.claims", "Claims")}
              columns={claimsColumns}
              data={sinistres}
              labelButtonFooter={t("panel.claims.seeAllClaims", "See all claims")}
              tableMaxHeight="234px"
              tableMinHeight="234px"
              showPagination={false}
              onClickButtonFooter={() => navigate(paths.claims.list)}
            />
          </Grid>
        </Grid>
      </Box>
    </Sidebar>
  );
};
